import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {startOfMonth} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {formatTaxPeriod, formatThreshold, localizeTitle} from '@entities/catalogs/lib';
import {ViolationThresholdsGate, violationThresholdsModel} from '@entities/catalogs/model';
import {ViolationThresholdDto} from '@entities/catalogs/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {DatePicker} from '@shared/ui/DatePicker';
import {Icon} from '@shared/ui/Icon';
import {Input} from '@shared/ui/Input';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {
    Actions,
    ControlsCard,
    FieldError,
    FilterBox,
    InlineField,
    RowActions,
    Wrapper,
} from './styled';

type ThresholdFormValues = {
    violationType: string;
    taxPeriod: Date | null;
    threshold: string;
};

const defaultValues: ThresholdFormValues = {
    violationType: '',
    taxPeriod: new Date(),
    threshold: '',
};

const schema = (t: (key: string) => string, isNewEntity: boolean) =>
    yup.object().shape({
        violationType: isNewEntity
            ? yup.string().required(t('select-violation-type'))
            : yup.string(),
        taxPeriod: isNewEntity
            ? yup
                  .date()
                  .nullable()
                  .required(t('select-tax-period'))
            : yup.date().nullable(),
        threshold: yup
            .string()
            .required(t('enter-threshold-amount'))
            .matches(/^\d{1,8}(\.\d{1,2})?$/, t('enter-threshold-amount')),
    });

export const ViolationThresholdsTab = () => {
    const {t, i18n} = useTranslation();
    const model = useUnit(violationThresholdsModel);
    const [editId, setEditId] = React.useState<string | null>(null);

    useGate(ViolationThresholdsGate);

    const translateValidates = React.useCallback(
        (key: string) => t(`page_audit.page_catalogs.violation-thresholds.validation.${key}`),
        [t],
    );
    const isNewEntity = editId === 'new';
    const {control, handleSubmit, errors, reset} = useForm<ThresholdFormValues>({
        resolver: yupResolver(schema(translateValidates, isNewEntity)),
        mode: 'all',
        defaultValues,
    });

    const violationTypeOptions = React.useMemo(
        () =>
            model.state.violationTypes.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, model.state.violationTypes],
    );

    const selectedViolationTypeFilter = React.useMemo(
        () =>
            violationTypeOptions.filter((item) =>
                model.state.filters.violationTypeCodes.includes(item.value),
            ),
        [model.state.filters.violationTypeCodes, violationTypeOptions],
    );

    const startCreate = React.useCallback(() => {
        reset(defaultValues);
        setEditId('new');
    }, [reset]);

    const startEdit = React.useCallback(
        (row: ViolationThresholdDto) => {
            const violationType = model.state.violationTypes.find(
                (item) => item.id === row.violationType,
            );
            reset({
                violationType: violationType?.code ?? '',
                taxPeriod: new Date(row.taxPeriodYear, row.taxPeriodMonth - 1, 1),
                threshold: String(row.threshold ?? ''),
            });
            setEditId(row.id);
        },
        [model.state.violationTypes, reset],
    );

    const cancel = React.useCallback(() => {
        setEditId(null);
        reset(defaultValues);
    }, [reset]);

    const onSubmit = React.useCallback(
        (values: ThresholdFormValues) => {
            const violationType = model.state.violationTypes.find(
                (item) => item.code === values.violationType,
            );
            const currentRow = model.state.data?.data.find((item) => item.id === editId);
            const taxPeriod =
                values.taxPeriod ??
                (currentRow
                    ? new Date(currentRow.taxPeriodYear, currentRow.taxPeriodMonth - 1, 1)
                    : null);
            const violationTypeId = violationType?.id ?? currentRow?.violationType;

            if (!violationTypeId || !taxPeriod) return;

            model.save({
                id: editId ?? undefined,
                payload: {
                    violationType: violationTypeId,
                    taxPeriodYear: taxPeriod.getFullYear(),
                    taxPeriodMonth: taxPeriod.getMonth() + 1,
                    threshold: values.threshold,
                },
            });
            cancel();
        },
        [cancel, editId, model],
    );

    const data = React.useMemo(() => {
        const rows = (model.state.data?.data ?? []).map((row: ViolationThresholdDto) => {
            const violationType = model.state.violationTypes.find(
                (item) => item.id === row.violationType,
            );
            const isEditable = editId === row.id;
            const isNewEditableRow = isEditable && editId === 'new';

            return {
                ...row,
                violationType: isNewEditableRow ? (
                    <EditableViolationType
                        control={control}
                        errors={errors}
                        options={violationTypeOptions}
                        pending={model.state.pending}
                        t={t}
                    />
                ) : (
                    localizeTitle(violationType?.title, i18n.language)
                ),
                taxPeriod: isNewEditableRow ? (
                    <EditableTaxPeriod
                        control={control}
                        errors={errors}
                        pending={model.state.pending}
                        t={t}
                    />
                ) : (
                    formatTaxPeriod(row.taxPeriodYear, row.taxPeriodMonth)
                ),
                threshold: isEditable ? (
                    <EditableThreshold
                        control={control}
                        errors={errors}
                        pending={model.state.pending}
                        t={t}
                    />
                ) : (
                    formatThreshold(row.threshold)
                ),
                actions: (
                    <RowActions>
                        {!isEditable ? (
                            <Button
                                type="button"
                                size="sm"
                                variant={ButtonVariant.Text}
                                onClick={() => startEdit(row)}
                            >
                                <Icon name="settings" width={16} color="#4F79D0" />
                            </Button>
                        ) : null}
                    </RowActions>
                ),
            };
        });

        if (editId === 'new') {
            return [
                {
                    id: 'new',
                    violationType: (
                        <EditableViolationType
                            control={control}
                            errors={errors}
                            options={violationTypeOptions}
                            pending={model.state.pending}
                            t={t}
                        />
                    ),
                    taxPeriod: (
                        <EditableTaxPeriod
                            control={control}
                            errors={errors}
                            pending={model.state.pending}
                            t={t}
                        />
                    ),
                    threshold: (
                        <EditableThreshold
                            control={control}
                            errors={errors}
                            pending={model.state.pending}
                            t={t}
                        />
                    ),
                    actions: null,
                },
                ...rows,
            ];
        }

        return rows;
    }, [
        control,
        editId,
        errors,
        i18n.language,
        model.state.data,
        model.state.pending,
        model.state.violationTypes,
        startEdit,
        t,
        violationTypeOptions,
    ]);

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                [
                    {
                        id: 'violationType',
                        header: t('page_audit.terms.violation-type'),
                        minWidth: 300,
                    },
                    {
                        id: 'taxPeriod',
                        header: t('page_audit.terms.tax-period'),
                        width: 240,
                        isSortable: true,
                    },
                    {
                        id: 'threshold',
                        header: t('page_audit.terms.threshold-sum'),
                        width: 240,
                        isSortable: true,
                    },
                    {id: 'actions', header: '', width: 80},
                ],
                undefined,
                undefined,
                undefined,
                model.state.sortings,
                model.sortingsChanged,
            ),
        [model.sortingsChanged, model.state.sortings, t],
    );

    return (
        <Wrapper>
            <ControlsCard>
                <FilterBox>
                    <Select
                        isMulti
                        isClearable
                        size="sm"
                        options={violationTypeOptions}
                        value={selectedViolationTypeFilter}
                        placeholder={t('page_audit.terms.violation-type')}
                        onChange={(value: unknown) =>
                            model.violationTypeCodesChanged(
                                ((value as Array<SelectOption<string>>) ?? [])
                                    .map((item) => item.value)
                                    .filter((item): item is string => Boolean(item)),
                            )
                        }
                    />
                </FilterBox>
                <FilterBox>
                    <MonthTaxPeriodPicker
                        selected={model.state.filters.taxPeriod}
                        onChange={model.taxPeriodChanged}
                        placeholderText={t('page_audit.terms.tax-period')}
                    />
                </FilterBox>
                <Actions>
                    {editId ? (
                        <>
                            <Button
                                size="sm"
                                type="button"
                                variant={ButtonVariant.Outline}
                                disabled={model.state.pending}
                                onClick={cancel}
                            >
                                {t('actions.cancel')}
                            </Button>
                            <Button
                                size="sm"
                                type="submit"
                                form="violation-threshold-form"
                                isLoading={model.state.pending}
                                disabled={model.state.pending}
                            >
                                {t('actions.save')}
                            </Button>
                        </>
                    ) : (
                        <Button type="button" onClick={startCreate} size="sm">
                            New threshold
                        </Button>
                    )}
                </Actions>
            </ControlsCard>
            <form id="violation-threshold-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <Table
                    columns={columns}
                    data={data}
                    isLoading={model.state.pending}
                    tableViewExtraSettings={{overflowX: 'visible'}}
                    pageCount={Math.ceil((model.state.data?.total ?? 0) / model.state.pageSize)}
                    pageIndex={model.state.pageNumber}
                    totalRowsCount={model.state.data?.total ?? 0}
                    onPageChange={model.pageNumberChanged}
                    onPageSizeChange={model.pageSizeChanged}
                />
            </form>
        </Wrapper>
    );
};

const EditableViolationType = ({control, errors, options, pending, t}: any) => (
    <InlineField>
        <Controller
            name="violationType"
            control={control}
            render={({onChange, value}: any) => (
                <Select
                    size="sm"
                    options={options}
                    value={options.find((item: SelectOption<string>) => item.value === value)}
                    onChange={(option: unknown) =>
                        onChange((option as SelectOption<string> | null)?.value ?? '')
                    }
                    hasError={Boolean(errors.violationType)}
                    disabled={pending}
                    placeholder={t('page_audit.terms.violation-type')}
                />
            )}
        />
        {errors.violationType && <FieldError>{errors.violationType.message}</FieldError>}
    </InlineField>
);

const EditableTaxPeriod = ({control, errors, pending, t}: any) => (
    <InlineField>
        <Controller
            name="taxPeriod"
            control={control}
            render={({onChange, value}: any) => (
                <MonthTaxPeriodPicker
                    selected={value}
                    onChange={(date) => onChange(date)}
                    disabled={pending}
                    hasError={Boolean(errors.taxPeriod)}
                    placeholderText={t('page_audit.terms.tax-period')}
                />
            )}
        />
        {errors.taxPeriod && <FieldError>{errors.taxPeriod.message}</FieldError>}
    </InlineField>
);

const MonthTaxPeriodPicker = ({
    selected,
    onChange,
    ...props
}: {
    selected?: Date | null;
    onChange: (date: Date | null) => void;
    disabled?: boolean;
    hasError?: boolean;
    placeholderText?: string;
}) => (
    <DatePicker
        selected={selected}
        size="sm"
        onChange={(date) => onChange(date ? startOfMonth(date) : null)}
        showMonthYearPicker
        dateFormat="MM.yyyy"
        {...props}
    />
);

const EditableThreshold = ({control, errors, pending, t}: any) => (
    <InlineField>
        <Controller
            name="threshold"
            control={control}
            render={({onChange, value, name}: any) => (
                <Input
                    size="sm"
                    style={{width: 200}}
                    name={name}
                    value={value}
                    onChange={onChange}
                    hasError={Boolean(errors.threshold)}
                    disabled={pending}
                    placeholder={t('page_audit.terms.threshold-sum')}
                />
            )}
        />
        {/*{errors.threshold && <FieldError>{errors.threshold.message}</FieldError>}*/}
    </InlineField>
);
